/**
 * HpStateMapperPicker  (SM-22 / DA-2295)
 *
 * Choiceless NUMBER/2D/3D HP questions have no choices to build clauses on — the
 * question's `stateMapper` *is* the entire read+write mapper, and it's just one
 * attribute: `HP-<column>` (§3.3 / §5). One catalog-backed picker is the whole builder
 * for this case; no cascade needed.
 *
 * The state key uses the mapper COLUMN from the tag (`HP-livingArea`), not the display
 * `attributeId` — same rule as the clause rows (see ./hpTag.ts).
 */
import React from 'react'
import { SxProps, Theme } from '@mui/material'
import { SearchableSelect } from 'components/common/SearchableSelect'
import { MapperCatalog } from 'types/apis/surveyInterfaces'
import { hpColumnOptions } from './hpTag'
import { StateMapperPreview } from './StateMapperPreview'

export interface HpStateMapperPickerProps {
  value?: string
  onChange: (stateMapper: string) => void
  catalog?: MapperCatalog
  loading?: boolean
  /** Catalog fetch failure, surfaced inline so it can't be misread as "no attributes". */
  error?: string
  disabled?: boolean
  /** SM-25: modified-field highlight, same treatment as the raw field this replaces. */
  sx?: SxProps<Theme>
}

const HP_PREFIX = 'HP-'

export const HpStateMapperPicker: React.FC<HpStateMapperPickerProps> = ({ value, onChange, catalog, loading, error, disabled, sx }) => {
  const column = value?.startsWith(HP_PREFIX) ? value.slice(HP_PREFIX.length) : ''
  const options = hpColumnOptions(catalog?.homeProfileAttributes)

  // error/empty/loading all read differently — and the message goes THROUGH SearchableSelect so
  // MUI links it to the input via aria-describedby (announced on focus, unlike a sibling helper).
  const isEmpty = !loading && !error && options.length === 0
  const helperText = error || (isEmpty ? 'No HP attributes available for this account type' : undefined)

  return (
    <>
      <SearchableSelect
        label="HP Attribute"
        value={column}
        onChange={(v) => onChange(v ? `${HP_PREFIX}${v}` : '')}
        options={options}
        disabled={disabled}
        loading={loading}
        error={!!error}
        helperText={helperText}
        noOptionsText={error ? 'Couldn’t load catalog' : 'No attributes available'}
        placeholder={loading ? 'Loading catalog…' : 'Select an attribute'}
        fullWidth
        sx={sx}
      />
      <StateMapperPreview value={value} />
    </>
  )
}
